import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { initGA, loadGAIfConsented, CONSENT_KEY } from '../../lib/ga';

const defaultConsent = { necessary: true, analytics: false, marketing: false };

const loadConsent = () => {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveConsent = (consent) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, date: new Date().toISOString() }));
};

const CookieConsent = () => {
  const [visible, setVisible]   = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs, setPrefs]       = useState(defaultConsent);

  useEffect(() => {
    if (!loadConsent()) setVisible(true);
    else loadGAIfConsented(); // Utilisateur de retour ayant déjà consenti
  }, []);

  const acceptAll = () => {
    saveConsent({ necessary: true, analytics: true, marketing: false });
    initGA(); // Charger GA4 immédiatement après consentement
    setVisible(false);
  };

  const rejectAll = () => {
    saveConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  };

  const saveCustom = () => {
    saveConsent(prefs);
    if (prefs.analytics) initGA(); // Charger GA4 si analytics acceptés
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Gestion des cookies"
      className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 shadow-2xl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {!expanded ? (
          /* Vue compacte */
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="flex items-start gap-3 flex-1">
              <Cookie className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">
                Nous utilisons des cookies strictement nécessaires au fonctionnement de la plateforme (session, authentification).
                Consultez notre{' '}
                <Link to="/privacy" className="text-primary underline hover:no-underline" onClick={() => setVisible(false)}>
                  politique de confidentialité
                </Link>.
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => setExpanded(true)}
                className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Settings className="w-4 h-4" /> Personnaliser
              </button>
              <button
                onClick={rejectAll}
                className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Refuser
              </button>
              <button
                onClick={acceptAll}
                className="flex items-center gap-1 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                <Check className="w-4 h-4" /> Accepter
              </button>
            </div>
          </div>
        ) : (
          /* Vue détaillée */
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                <Cookie className="w-5 h-5 text-primary" /> Paramètres des cookies
              </h2>
              <button onClick={() => setExpanded(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="space-y-3 mb-4">
              {[
                {
                  key:      'necessary',
                  label:    'Cookies nécessaires',
                  desc:     'Session, authentification, sécurité CSRF. Obligatoires — non désactivables.',
                  disabled: true,
                },
                {
                  key:   'analytics',
                  label: 'Cookies analytiques',
                  desc:  'Google Analytics — mesure d\'audience pour améliorer la plateforme. Données transmises à Google LLC (policies.google.com/privacy).',
                },
                {
                  key:   'marketing',
                  label: 'Cookies marketing',
                  desc:  'Publicités personnalisées. Non utilisés actuellement.',
                },
              ].map(({ key, label, desc, disabled }) => (
                <div key={key} className="flex items-start justify-between gap-4 p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{label}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-0.5">
                    <input
                      type="checkbox"
                      checked={prefs[key]}
                      disabled={disabled}
                      onChange={e => setPrefs(p => ({ ...p, [key]: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className={`w-10 h-6 rounded-full peer peer-checked:bg-primary bg-gray-300
                      after:content-[''] after:absolute after:top-[2px] after:left-[2px]
                      after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all
                      peer-checked:after:translate-x-4 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                  </label>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={rejectAll} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">
                Tout refuser
              </button>
              <button onClick={saveCustom} className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90">
                Enregistrer mes choix
              </button>
              <button onClick={acceptAll} className="flex items-center gap-1 px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90">
                <Check className="w-4 h-4" /> Tout accepter
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
